
"use client"

import { useState, useEffect } from "react"
import { m, AnimatePresence } from "framer-motion"
import { Check, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { PriceTick as Icon_PriceTick } from "@/components/customer/icons/PriceTick";
const SvgIcons = { PriceTick: Icon_PriceTick };
import Link from "next/link"
import top from "@/public/images/VilvaD2C/DotImg.webp"
import bottom from "@/public/images/VilvaD2C/DotImg.webp"
import Image from "next/image"

interface PricingPlan {
  id: string
  name: string
  monthlyPrice: number
  yearlyPrice: number
  description: string
  features: {
    name: string
    basic: string | boolean
    growth: string | boolean
    premium: string | boolean
  }[]
  isRecommended?: boolean
}

const pricingData: PricingPlan = {
  id: "pricing",
  name: "Pricing Plans",
  monthlyPrice: 0,
  yearlyPrice: 0,
  description: "",
  features: [
    {
      name: "Storefront Design",
      basic: "Template Selection",
      growth: "Customizable Templates",
      premium: "Tailored Premium UI Design",
    },
    {
      name: "Product Listings",
      basic: "Up to 500",
      growth: "Unlimited",
      premium: "Unlimited",
    },
    {
      name: "Product Categories",
      basic: "Up to 20",
      growth: "Unlimited",
      premium: "Unlimited",
    },
    {
      name: "Discount Coupons",
      basic: "Up to 10",
      growth: "Unlimited",
      premium: "Unlimited",
    },
    {
      name: "Customer Registrations",
      basic: "Unlimited",
      growth: "Unlimited",
      premium: "Unlimited",
    },
    {
      name: "Payment Gateway Integration",
      basic: true,
      growth: true,
      premium: true,
    },
    {
      name: "Courier & Logistics Integration",
      basic: true,
      growth: true,
      premium: true,
    },
    {
      name: "Abandoned Cart Recovery",
      basic: "Basic",
      growth: "Smart Tools",
      premium: "Full Automated",
    },
    {
      name: "Mobile App",
      basic: false,
      growth: false,
      premium: "Branded Mobile App",
    },
    {
      name: "Product Upload Assistance",
      basic: false,
      growth: false,
      premium: "Up to 100 Products",
    },
    {
      name: "Business Setup Support (Worth ₹15,000)",
      basic: false,
      growth: "Included",
      premium: "Included",
    },
    {
      name: "Google My Business Setup",
      basic: false,
      growth: true,
      premium: true,
    },
    {
      name: "Meta (FB/Instagram) Account Setup",
      basic: false,
      growth: true,
      premium: true,
    },
    {
      name: "Marketing Support (Worth ₹10,000)",
      basic: false,
      growth: false,
      premium: "Free for 2 Months",
    },
    {
      name: "Payment Flexibility",
      basic: "One-Time",
      growth: "One-Time or Monthly",
      premium: "One-Time",
    },
  ],
  isRecommended: false,
}

const plans = [
  {
    id: "basic",
    name: "Essential Plan",
    monthlyPrice: 0,
    yearlyPrice: 9999,
    description: "Kickstart your online store with essential tools simple, smart, and affordable.",
    isRecommended: false,
    isHide: true,
  },
  {
    id: "growth",
    name: "Growth Plan",
    monthlyPrice: 19999,
    yearlyPrice: 19999,
    description: "Scale your business with advanced features and flexible payment options.",
    isRecommended: true,
    isHide: false,
  },
  {
    id: "premium",
    name: "Premium Plan",
    monthlyPrice: 0,
    yearlyPrice: 39999,
    description: "Stand out with custom design, mobile app, and expert marketing support.",
    isRecommended: false,
    isHide: true,
  },
]

export default function EcomPricingPlan() {
  const [isYearly, setIsYearly] = useState(true)
  const [selectedMobilePlan, setSelectedMobilePlan] = useState<"basic" | "growth" | "premium">("growth")

  useEffect(() => {
    if (!isYearly) {
      setSelectedMobilePlan("growth");
    }
  }, [isYearly])

  const formatPrice = (price: number) => {
    return `₹${price.toLocaleString()}`
  }

  const renderFeatureValue = (value: string | boolean, planType: "basic" | "growth" | "premium") => {
    if (typeof value === "boolean") {
      return value ? <div className="flex justify-center items-center"> <SvgIcons.PriceTick /></div> : <div className="flex justify-center items-center"> <X className="w-5 h-5 text-gray-400" /></div>
    }
    return <span className="text-[#252430] text-[14px] md:text-[16px] font-normal leading-[26px] font-Schibsted_Grotesk">{value}</span>
  }

  const getCurrentMobilePlan = () => {
    if (selectedMobilePlan === "basic") return plans[0]
    if (selectedMobilePlan === "growth") return plans[1]
    return plans[2] // premium
  }

  return (
    <div className="min-h-screen  py-[60px] md:py-[120px] px-4 relative bg-[#14243C]">
      <div className="max-w-7xl mx-auto relative z-20 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-white text-center text-[24px] mb-[16px] md:text-[40px] font-normal leading-[120%] font-NeueMontreal">
            We Grow When Your Business Grows
          </h2>
          <p className="text-[#CDDFDB] text-center text-[18px] font-normal leading-[150%] font-Schibsted_Grotesk mb-[24px]">
            Enhance your online presence with a stunning profile and premium visuals that captivate and engage.
          </p>

          {/* Monthly/Yearly Toggle */}
          <div className="flex items-center bg-white rounded-[28px] w-fit mx-auto justify-center gap-[12px] mb-[12px] p-[2px]">
            <m.button
              onClick={() => setIsYearly(false)}
              className={`px-6 py-[12px] text-[14px] font-normal leading-[100%] font-Schibsted_Grotesk rounded-full  transition-colors  ${!isYearly ? "bg-[#1667C3] text-white " : "bg-transparent text-[#1667C3]"
                }`}
              // whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Monthly
            </m.button>
            <m.button
              onClick={() => setIsYearly(true)}
              className={`px-6 py-[12px] text-[14px] font-normal leading-[100%] font-Schibsted_Grotesk rounded-full  transition-colors ${isYearly ? "bg-[#1667C3] text-white " : "bg-transparent text-[#1667C3]"
                }`}
              // whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Yearly
            </m.button>
          </div>

          <p className="text-[#FFFFFF] text-[14px] font-normal mb-[42px] leading-[100%] font-Schibsted_Grotesk">Monthly plan only available for ₹19,999</p>
        </div>

        {/* Desktop View */}
        <div className="hidden md:block">
          <AnimatePresence mode="wait">
            <m.div
              key={isYearly ? "yearly" : "monthly"}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-3 items-end gap-8 mb-12"
            >
              {plans.map((plan) => (
                <m.div
                  key={plan.id}

                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className={`relative bg-white ${plan.isHide && !isYearly ? 'hidden' : 'block'} rounded-[16px] p-8 shadow-xl ${plan.isRecommended ? "ring-[2px] ring-[#4F85D6] rounded-[16px] border-[2px] border-[#4F85D6] !shadow-[0_70px_20px_0_rgba(0,97,158,0.01),0_45px_18px_0_rgba(0,97,158,0.06),0_25px_15px_0_rgba(0,97,158,0.20),0_11px_11px_0_rgba(0,97,158,0.34),0_3px_6px_0_rgba(0,97,158,0.39)]" : "" }`}>
                    <div>
                      {plan.isRecommended && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="inline-flex justify-center items-center gap-[10px] px-[12px] py-[8px] rounded-[30px] bg-[#1667C3] text-white text-center text-[12px] font-normal leading-[100%] font-Schibsted_Grotesk">
                            Recommended Plan
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-[#00073C] mb-[16px] text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk">{plan.name}</h3>
                    <div className="text-[#1667C3] text-[28px]  mb-[16px] font-[700] leading-[100%] font-Schibsted_Grotesk">
                      {formatPrice(isYearly ? plan.yearlyPrice : plan.monthlyPrice)}
                      <span className="text-[#00000080] text-[14px] font-normal leading-[150%] font-Schibsted_Grotesk"> / {isYearly ? "Yearly" : "Monthly"}</span>
                    </div>
                    <p className="text-[#000000]/80 text-[14px] font-normal leading-[150%] font-Schibsted_Grotesk">{plan.description}</p>

                    <div className="my-4 h-[1px] bg-[#000000]/10" />

                    <Link href='https://d2c-e-commerce-platform-for-growth-stage-businesses.vilvabusiness.com/register' target="_blank" className="w-full  hover:bg-[#4F85D6]  flex h-[44px] px-[32px] py-[20px] justify-center items-center gap-[10px] self-stretch rounded-[8px] bg-[#1667C3] text-white text-center text-[18px] font-normal leading-[100%] font-Schibsted_Grotesk"
                    >
                      Get Plan Now
                    </Link>



                    {plan.isRecommended && (
                      <p className="text-[#394442] text-center mt-[16px] text-[14px] font-normal leading-[100%] font-Schibsted_Grotesk">You Saved ₹4000</p>
                    )}
                  </div>
                </m.div>
              ))}
            </m.div>
          </AnimatePresence>

          {/* Desktop Feature Comparison Table */}
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className={`bg-white ${!isYearly ? "w-[600px]" : "w-fill"} mx-auto rounded-[16px] shadow-xl overflow-hidden`}
          >
            <div className="overflow-x-auto">
              <table className="w-full ">
                <thead className="border-b border-b-[#E0F1EE]">
                  <tr>
                    <th className="text-[#252430] py-4 px-4  text-[18px] font-normal leading-[100%] font-Schibsted_Grotesk">Plans</th>
                    <th className={` ${!isYearly ? 'hidden' : ''} text-[#252430] py-4  text-center text-[18px] font-normal leading-[100%] font-Schibsted_Grotesk`}>
                      Basic Plan
                      <span className="text-[#1667C3] py-4 pl-[10px] font-[700] text-[18px] leading-[100%] font-Schibsted_Grotesk">₹ 9,999</span>
                    </th>
                    <th className={` text-[#252430] py-4  text-center text-[18px] bg-[#F2F6FC] !shadow-[inset_0_0_80px_0_rgba(12,123,213,0.20)] font-normal leading-[100%] font-Schibsted_Grotesk`}>
                      Growth Plan
                      <span className="text-[#1667C3] py-4 pl-[10px] font-[700] text-[18px] leading-[100%] font-Schibsted_Grotesk">₹
                        {isYearly ? "19,999" : "19,999"}</span>
                    </th>
                    <th className={` ${!isYearly ? 'hidden' : ''} text-[#252430] py-4  text-center text-[18px] font-normal leading-[100%] font-Schibsted_Grotesk`}>
                      Premium Plan
                      <span className="text-[#1667C3] py-4 pl-[10px] font-[700] text-[18px] leading-[100%] font-Schibsted_Grotesk">₹ 39,999</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pricingData.features.map((feature, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-[#fff]" : "bg-[#F2F6FC]"}>
                      <td className="py-4 px-6 text-[16px]  font-normal leading-[26px] font-Schibsted_Grotesk  text-[#252430]">{feature.name}</td>
                      <td className={`${!isYearly ? 'hidden' : 'block'}  py-[20px] px-6 text-[16px] font-normal leading-[26px] font-Schibsted_Grotesk  text-center`}>{renderFeatureValue(feature.basic, "basic")}</td>
                      <td className="py-4 px-6 text-[16px] !shadow-[inset_0_0_80px_0_rgba(12,123,213,0.20)] bg-[#FFFFFF] font-normal leading-[26px] font-Schibsted_Grotesk text-center">{renderFeatureValue(feature.growth, "growth")}
                      </td>
                      <td className={`${!isYearly ? 'hidden' : 'block'} py-4 px-6 text-[16px] font-normal leading-[26px] font-Schibsted_Grotesk text-center`}>{renderFeatureValue(feature.premium, "premium")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </m.div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          {/* Mobile Plan Toggle */}
          <div className="flex justify-center gap-2 mb-8">
            {!isYearly ? (
              // Monthly view - only Basic plan available and active
              <div className="px-6 py-[10px] pb-[12px] text-[#000] text-center text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk rounded-full bg-white ">Basic</div>
            ) : (
              // Yearly view - all plans available
              <>
                <div className="bg-[#1667C3] rounded-[28px] p-[4px]">
                  <m.button
                    onClick={() => setSelectedMobilePlan("basic")}
                    className={`px-6 py-[10px] rounded-full border-none text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk transition-colors ${selectedMobilePlan === "basic"
                      ? "bg-white text-[#1667C3]"
                      : "bg-transparent text-white "
                      }`}

                    whileTap={{ scale: 0.95 }}
                  >
                    Basic
                  </m.button>
                  <m.button
                    onClick={() => setSelectedMobilePlan("growth")}
                    className={`px-6 py-[10px] rounded-full border-none text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk transition-colors ${selectedMobilePlan === "growth"
                      ? "bg-white text-[#1667C3]"
                      : "bg-transparent text-white "
                      }`}

                    whileTap={{ scale: 0.95 }}
                  >
                    Growth
                  </m.button>
                  <m.button
                    onClick={() => setSelectedMobilePlan("premium")}
                    className={`px-6 py-[10px] rounded-full border-none text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk transition-colors ${selectedMobilePlan === "premium"
                      ? "bg-white text-[#1667C3]"
                      : "bg-transparent text-white "
                      }`}

                    whileTap={{ scale: 0.95 }}
                  >
                    Premium
                  </m.button>
                </div>
              </>
            )}
          </div>

          <AnimatePresence mode="wait">
            <m.div
              key={`${selectedMobilePlan}-${isYearly}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Mobile Plan Card */}
              <div className="">
                <div className={`relative bg-white ${getCurrentMobilePlan().isHide && !isYearly ? 'hidden' : 'block'} rounded-[16px] p-8 shadow-xl ${getCurrentMobilePlan().isRecommended ? "ring-[2px] ring-[#0CD5AB]  border-2 rounded-[16px] border-[#0CD5AB] !bg-[linear-gradient(205deg,_#FFF_5.43%,_#F0FCFF_50.3%,_#E0FFF9_102.98%)] !shadow-[0px_70px_20px_0px_rgba(0,158,124,0.01),0px_45px_18px_0px_rgba(0,158,124,0.06),0px_25px_15px_0px_rgba(0,158,124,0.20),0px_11px_11px_0px_rgba(0,158,124,0.34),0px_3px_6px_0px_rgba(0,158,124,0.39)]" : ""
                  }`}>
                  <div>
                    {getCurrentMobilePlan().isRecommended && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="inline-flex justify-center items-center gap-[10px] px-[12px] py-[8px] rounded-[30px] bg-[#1667C3]  text-white text-center text-[12px] font-normal leading-[100%] font-Schibsted_Grotesk">
                          Recommended Plan
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-[#00073C] mb-[16px] text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk">{getCurrentMobilePlan().name}</h3>
                  <div className="text-[#019C7B] text-[28px]  mb-[16px] font-normal leading-[100%] font-LatinkaBold">
                    {formatPrice(isYearly ? getCurrentMobilePlan().yearlyPrice : getCurrentMobilePlan().monthlyPrice)}

                    <span className="text-[#699D9D] text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk"> / {isYearly ? "Yearly" : "Monthly"}</span>
                  </div>
                  <p className="text-[#394442] text-[14px] font-normal leading-[150%] font-Schibsted_Grotesk">{getCurrentMobilePlan().description}</p>

                  <div className="bg-[#C7ECF4] my-[16px] h-[0.06px]" />

                  <Link href='https://d2c-e-commerce-platform-for-growth-stage-businesses.vilvabusiness.com/register' target="_blank" className="w-full  hover:bg-emerald-700  flex h-[44px] px-[32px] py-[20px] justify-center items-center gap-[10px] self-stretch rounded-[8px] bg-[#1667C3] text-white text-center text-[18px] font-normal leading-[100%] font-Schibsted_Grotesk"
                  >
                    Get Plan Now
                  </Link>

                  {getCurrentMobilePlan().isRecommended && (
                    <p className="text-[#394442] text-center mt-[16px] text-[14px] font-normal leading-[100%] font-Schibsted_Grotesk">Choose Yearly Plan & Save ₹4000</p>
                  )}
                </div>



              </div>

              {/* Mobile Feature List */}
              <div className="bg-white  rounded-[16px] shadow-xl overflow-hidden">
                <div className="bg-gray-50  flex justify-between items-center">
                  <span className="text-[#252430] px-6 py-4 w-1/2 text-[16px] font-normal leading-[100%] font-Schibsted_Grotesk">Plans</span>
                  <span className={`text-[#252430] w-1/2 px-6 py-4 ${selectedMobilePlan === "growth" ? "shadow-[inset_5px_5px_54px_5px_rgba(12,213,171,0.15)]" : ""} text-center text-[16px] font-normal leading-[120%] font-Schibsted_Grotesk`}>
                    {selectedMobilePlan === "basic"
                      ? "Basic Plan"
                      : selectedMobilePlan === "growth"
                        ? "Growth Plan"
                        : "Premium Plan"}
                    <br />
                    <span className="text-[#019C7B] text-[16px] font-normal  leading-[100%] font-Schibsted_Grotesk">
                      {formatPrice(isYearly ? getCurrentMobilePlan().yearlyPrice : getCurrentMobilePlan().monthlyPrice)}
                    </span>
                  </span>
                </div>

                <div className="divide-y divide-gray-200 ">
                  {pricingData.features.map((feature, index) => (
                    <div key={index} className=" flex justify-between items-center">
                      <span className="font-medium px-4 w-1/2 text-gray-900 text-sm">{feature.name}</span>
                      <div className={`w-1/2 px-4 py-6 ${selectedMobilePlan === "growth" ? "shadow-[inset_5px_5px_54px_5px_rgba(12,213,171,0.15)]" : ""}`}>
                        <div className="text-center ">
                          {renderFeatureValue(
                            selectedMobilePlan === "basic"
                              ? feature.basic
                              : selectedMobilePlan === "growth"
                                ? feature.growth
                                : feature.premium,
                            selectedMobilePlan as "basic" | "growth" | "premium",
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </m.div>
          </AnimatePresence>
        </div>
      </div>
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 flex flex-col justify-between">
          <Image src={top} alt="image tag" />
        </div>
        <div className="absolute bottom-0 flex flex-col justify-between">
          <Image className="rotate-180" src={bottom} alt="image tag" />
        </div>
      </div>
    </div>
  )
}
